import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { useTrafficTracker } from "@/hooks/useTrafficTracker";
import HomePage from "@/pages/Home";
import ServicesPage from "@/pages/Services";
import ServiceAreaPage from "@/pages/ServiceArea";
import AboutPage from "@/pages/About";
import ContactPage from "@/pages/Contact";
import CareersPage from "@/pages/Careers";
import DiagnosticPage from "@/pages/Diagnostic";
import DepositPage from "@/pages/Deposit";
import ReviewsPage from "@/pages/Reviews";
import EstimatePage from "@/pages/Estimate";
import ShopPage from "@/pages/Shop";
import ReferPage from "@/pages/Refer";
import PrivacyPage from "@/pages/Privacy";
import TermsPage from "@/pages/Terms";
import PaymentSuccessPage from "@/pages/PaymentSuccess";
import LoginPage from "@/pages/Login";
import AdminLoginPage from "@/pages/AdminLogin";
import AdminPage from "@/pages/Admin";
import ApplianceInstallationGuide from "@/pages/guides/ApplianceInstallationGuide";
import CourierLogisticsGuide from "@/pages/guides/CourierLogisticsGuide";
import HomePreservationGuide from "@/pages/guides/HomePreservationGuide";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [pathname]);
  return null;
};

const TrafficTracker = () => {
  useTrafficTracker();
  return null;
};

const NotFound = () => (
  <div data-testid="not-found" className="ji-container py-32 text-center">
    <div className="ji-overline mb-4">404</div>
    <h1 style={{ fontFamily: "Cabinet Grotesk" }} className="text-5xl font-bold tracking-tight">Page not found.</h1>
    <p className="mt-4 text-[var(--ji-muted)]">The page you're looking for doesn't exist.</p>
    <a href="/" className="ji-btn-primary mt-8 inline-flex items-center px-6 h-12 text-sm font-semibold" style={{ borderRadius: 4 }}>Back home</a>
  </div>
);

const LayoutWithChrome = ({ children }) => (
  <>
    <Header />
    <main>{children}</main>
    <Footer />
  </>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <TrafficTracker />
          <Routes>
            <Route path="/" element={<LayoutWithChrome><HomePage /></LayoutWithChrome>} />
            <Route path="/services" element={<LayoutWithChrome><ServicesPage /></LayoutWithChrome>} />
            <Route path="/service-area" element={<LayoutWithChrome><ServiceAreaPage /></LayoutWithChrome>} />
            <Route path="/about" element={<LayoutWithChrome><AboutPage /></LayoutWithChrome>} />
            <Route path="/contact" element={<LayoutWithChrome><ContactPage /></LayoutWithChrome>} />
            <Route path="/careers" element={<LayoutWithChrome><CareersPage /></LayoutWithChrome>} />
            <Route path="/diagnostic" element={<LayoutWithChrome><DiagnosticPage /></LayoutWithChrome>} />
            <Route path="/deposit" element={<LayoutWithChrome><DepositPage /></LayoutWithChrome>} />
            <Route path="/reviews" element={<LayoutWithChrome><ReviewsPage /></LayoutWithChrome>} />
            <Route path="/estimate" element={<LayoutWithChrome><EstimatePage /></LayoutWithChrome>} />
            <Route path="/shop" element={<LayoutWithChrome><ShopPage /></LayoutWithChrome>} />
            <Route path="/refer" element={<LayoutWithChrome><ReferPage /></LayoutWithChrome>} />
            <Route path="/privacy" element={<LayoutWithChrome><PrivacyPage /></LayoutWithChrome>} />
            <Route path="/terms" element={<LayoutWithChrome><TermsPage /></LayoutWithChrome>} />
            <Route path="/guides/appliance-installation" element={<LayoutWithChrome><ApplianceInstallationGuide /></LayoutWithChrome>} />
            <Route path="/guides/courier-logistics" element={<LayoutWithChrome><CourierLogisticsGuide /></LayoutWithChrome>} />
            <Route path="/guides/home-preservation" element={<LayoutWithChrome><HomePreservationGuide /></LayoutWithChrome>} />
            <Route path="/payments/success" element={<LayoutWithChrome><PaymentSuccessPage /></LayoutWithChrome>} />
            <Route path="/square/success" element={<LayoutWithChrome><PaymentSuccessPage /></LayoutWithChrome>} />
            <Route path="/login" element={<LayoutWithChrome><LoginPage /></LayoutWithChrome>} />
            <Route path="/admin/login" element={<LayoutWithChrome><AdminLoginPage /></LayoutWithChrome>} />
            <Route
              path="/admin"
              element={
                <LayoutWithChrome>
                  <ProtectedRoute><AdminPage /></ProtectedRoute>
                </LayoutWithChrome>
              }
            />
            <Route path="*" element={<LayoutWithChrome><NotFound /></LayoutWithChrome>} />
          </Routes>
          <Toaster richColors position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
